// Held items in the client (shared/held.ts): the slot on PvP line-ups (a badge on a creature's face, or a chip in a
// list) that opens the picker, the card on a creature's page, the Bag's list of finished items and the forge rows at
// the crafting bench. Nothing here shows while the admin switch is off.
import { HELD, HELD_ITEMS, HELD_BOSSES, PIECES_PER_HELD, forgeProblem, heldEffectText, heldName, heldPiece, heldRangeText, pieceChance, type HeldItem } from '../shared/held.ts';
import { heldScaleOf } from '../shared/pvpfight.ts';
import { ELEMENT_IDS, ELEMENTS } from '../shared/elements.ts';
import { SPECIES } from '../shared/species.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { iconUrl } from './assets.ts';
import { esc, full, cname } from './fmt.ts';
import { openDialog } from './dialog.ts';

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindHeld(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

/** The Spirit Bead's element at the bench, kept while the page re-renders. */
let forgeEl: string = ELEMENT_IDS[0];

export const heldIcon = (item: string, cls = 'held-ic', px = 24) => `<img class="${cls}" src="${iconUrl(item, px)}" alt="">`;
/** "Hearthstone Amulet 72%". */
export const heldLabel = (h: HeldItem) => `${heldName(h)} ${h.quality}%`;
/** What an item does in the live fight (scaled on the board, shared/board.ts heldScale): "+3.9% HP". */
export const heldText = (h: Pick<HeldItem, 'item' | 'quality' | 'el'>) => heldEffectText(h, heldScaleOf('live'));
/** An item's range from the lowest roll to quality 100 in the live fight: "+2-3.9% HP". */
export const heldRange = (item: string) => heldRangeText(item, heldScaleOf('live'));

let ownsMemo: { t: unknown; c: unknown; n: boolean } | null = null;
/** Whether line-ups show held slots: the switch is on and the trainer owns at least one item, worn or not. */
export function heldShown(): boolean {
  const t = S.trainer;
  if (!S.heldOn || !t) return false;
  if (ownsMemo?.t === t && ownsMemo.c === S.creatures) return ownsMemo.n;
  const n = (t.held?.length ?? 0) > 0 || [...S.creatures.values()].some((c) => !!c.held);
  ownsMemo = { t, c: S.creatures, n };
  return n;
}

const title = (c: CreatureDTO) => (c.held ? `${heldLabel(c.held)}: ${heldText(c.held)}` : `${cname(c)}: no held item`);
/** A held-item badge on a creature's face in a line-up (client/arena.ts teamFace extra); opens the picker. */
export function heldBadge(c: CreatureDTO): string {
  if (!heldShown()) return '';
  return `<button class="held-badge${c.held ? '' : ' empty'}" data-act="held-pick" data-id="${c.id}" title="${esc(title(c))}" aria-label="${esc(`Held item for ${cname(c)}`)}">${c.held ? heldIcon(c.held.item) : '+'}</button>`;
}
/** A held-item chip in a line-up list row: the item and its quality, or an empty slot; opens the picker. */
export function heldChip(c: CreatureDTO): string {
  if (!heldShown()) return '';
  return `<button class="held-chip${c.held ? '' : ' empty'}" data-act="held-pick" data-id="${c.id}" title="${esc(title(c))}">${c.held ? `${heldIcon(c.held.item)}<span>${esc(heldLabel(c.held))}</span>` : '<span>No held item</span>'}</button>`;
}

const optionText = (h: HeldItem) => `${heldLabel(h)} · ${heldText(h)}`;

/** The picker: put one of the trainer's items on the creature (swapping its own back), or take its item off. */
async function pick(id: number) {
  const c = S.creatures.get(id), t = S.trainer;
  if (!c || !t) return;
  const free = [...(t.held ?? [])].sort((a, b) => a.item.localeCompare(b.item) || b.quality - a.quality);
  const body = `${c.held ? `<p class="held-now">${heldIcon(c.held.item)}<span><b>${esc(heldLabel(c.held))}</b><br><span class="muted">${esc(heldText(c.held))}</span></span></p>` : ''}
    ${free.length ? `<label class="dlg-field"><span>${c.held ? 'Swap for' : 'Item'}</span><select name="held">${free.map((h) => `<option value="${esc(h.id)}">${esc(optionText(h))}</option>`).join('')}</select></label>` : `<p class="muted">${c.held ? 'No other items to swap in.' : 'No items free.'}</p>`}`;
  const r = await openDialog({
    title: `Held item · ${cname(c)}`, body,
    actions: [...(c.held ? [{ label: 'Take off', value: 'off' }] : []), ...(free.length ? [{ label: c.held ? 'Swap' : 'Hold', value: 'set', kind: 'primary' as const }] : [])],
  });
  if (r.value === 'off') send({ t: 'hold', id, held: null });
  else if (r.value === 'set' && r.data.held) send({ t: 'hold', id, held: r.data.held });
}

/** The held-item card on a creature's page. */
export function heldCard(c: CreatureDTO): string {
  const t = S.trainer!;
  if (!S.heldOn && !c.held) return '';
  const free = [...(t.held ?? [])].sort((a, b) => b.quality - a.quality);
  if (!c.held && !free.length) return '';
  return `<section class="panel cp-card"><div class="cp-h">${c.held ? heldIcon(c.held.item, 'cp-ico') : ''}<h5>Held item</h5><span class="muted">${c.held ? esc(heldLabel(c.held)) : '· empty slot'}</span></div>
    ${c.held ? `<p>${esc(heldText(c.held))}</p>` : ''}
    <div class="row">${free.length ? `<select data-hold="${c.id}" aria-label="Choose a held item" class="grow"${c.listed ? ' disabled' : ''}><option value="">${c.held ? 'Swap for…' : 'Choose an item…'}</option>${free.map((h) => `<option value="${esc(h.id)}">${esc(optionText(h))}</option>`).join('')}</select>` : ''}
    ${c.held ? `<button class="btn" data-act="held-off" data-id="${c.id}">Take off</button>` : ''}</div></section>`;
}

/** The Bag's finished items: free ones first (they can be listed), then the ones creatures wear. */
export function heldBagHtml(): string {
  const t = S.trainer!;
  const free = [...(t.held ?? [])].sort((a, b) => a.item.localeCompare(b.item) || b.quality - a.quality);
  const worn = [...S.creatures.values()].filter((c) => c.held).sort((a, b) => a.held!.item.localeCompare(b.held!.item));
  if (!free.length && !worn.length) return '';
  const row = (h: HeldItem, who: CreatureDTO | null) => `<li class="held-row">${heldIcon(h.item)}<span class="grow"><b>${esc(heldLabel(h))}</b><span class="muted">${esc(heldText(h))}${who ? ` · on ${esc(cname(who))}` : ''}</span></span>
    ${who ? `<a class="btn small" href="#/creature/${who.id}">Open</a>` : h.dev ? '' : `<button class="btn small" data-act="list-held" data-id="${esc(h.id)}">List on Market…</button>`}</li>`;
  return `<section class="panel held-bag"><h4>Held items</h4><ul class="held-list">${free.map((h) => row(h, null)).join('')}${worn.map((c) => row(c.held!, c)).join('')}</ul></section>`;
}

const BOSS_NAME = (b: string) => SPECIES[b]?.name ?? b;
/** The Spirit Bead's element picker, on the bench row and in the item's detail. */
const elSelect = () => `<select data-forge-el aria-label="Element">${ELEMENT_IDS.map((e) => `<option value="${e}"${e === forgeEl ? ' selected' : ''}>${ELEMENTS[e].name}</option>`).join('')}</select>`;
/** Whether ten pieces of this item are ready to forge now. */
const forgeReady = (id: string) => !forgeProblem(id, S.trainer!.bag, HELD[id].effect === 'element' ? forgeEl : undefined);
/** The Forge button, for the bench row and the item's detail. */
export const forgeButton = (id: string, cls = 'small') => `<button class="btn ${cls}${forgeReady(id) ? ' primary' : ''}" data-act="held-forge" data-id="${id}"${forgeReady(id) ? '' : ' disabled'}>Forge</button>`;
export const forgeElSelect = (id: string) => (HELD[id]?.effect === 'element' ? elSelect() : '');

/**
 * The held items at the crafting bench, one section per boss, as cards like the bench's recipes: the item, its
 * pieces and how many you hold. A card opens the item's detail (`h:<id>` in the Bag, `sel`) with its effect, where
 * the pieces drop and the Forge button; ten pieces forge one item, with a fresh quality roll every time.
 */
export function forgeHtml(sel = ''): string {
  if (!S.heldOn) return '';
  const t = S.trainer!;
  const owned = HELD_ITEMS.some((d) => (t.bag[heldPiece(d.id)] ?? 0) > 0);
  if (!owned) return '';
  return HELD_BOSSES.map((b) => `<section class="cr-sec forge-sec"><h4 class="sec">Held items · ${esc(BOSS_NAME(b))}</h4><div class="cr-grid">${HELD_ITEMS.filter((d) => d.boss === b).map((d) => {
    const have = t.bag[heldPiece(d.id)] ?? 0, on = sel === `h:${d.id}`, ok = have >= PIECES_PER_HELD;
    const n = Math.floor(have / PIECES_PER_HELD);
    const state = ok ? `<span class="cr-ok">Can forge ${full(n)}</span>` : `<span class="cr-why">${full(PIECES_PER_HELD - have)} more ${PIECES_PER_HELD - have === 1 ? 'piece' : 'pieces'}</span>`;
    return `<button class="bg-tile cr-card forge-card${on ? ' on' : ''}${ok ? '' : ' no'}" data-act="bag-pick" data-id="h:${d.id}" data-key="h:${d.id}" aria-pressed="${on}" title="${esc(`${d.name}: ${heldRange(d.id)}`)}">
      <span class="cr-row"><span class="cr-out"><img src="${iconUrl(d.id)}" alt=""></span><span class="cr-arrow" aria-hidden="true">←</span><span class="cr-ins"><span class="cr-in${ok ? '' : ' short'}" title="${esc(`${d.name} Piece: ${full(have)} of ${PIECES_PER_HELD}`)}"><img src="${iconUrl(heldPiece(d.id))}" alt="${esc(`${d.name} Piece`)}"><small>${full(have)}/${PIECES_PER_HELD}</small></span></span></span>
      <span class="cr-nm">${esc(d.name)}</span>${state}</button>`;
  }).join('')}</div></section>`).join('');
}

/** Held-item clicks (data-act="held-…"); true when handled. */
export function heldClick(el: HTMLElement): boolean {
  const id = el.dataset.id ?? '';
  switch (el.dataset.act) {
    case 'held-pick': void pick(Number(id)); return true;
    case 'held-off': send({ t: 'hold', id: Number(id), held: null }); return true;
    case 'held-forge': if (HELD[id]) send({ t: 'heldForge', item: id, ...(HELD[id].effect === 'element' ? { el: forgeEl } : {}) }); return true;
  }
  return false;
}

/** Held-item selects; true when handled. */
export function heldChange(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.hold) { if (el.value) send({ t: 'hold', id: Number(el.dataset.hold), held: el.value }); return true; }
  if (el.dataset.forgeEl !== undefined) { forgeEl = el.value; rerender(); return true; }
  return false;
}

/** How often wins drop a piece, for the help: boss wins and Boss Ladder steps, in one phrase when they match. */
function pieceRate(): string {
  const b = Math.round(pieceChance('boss') * 100), l = Math.round(pieceChance('ladder') * 100);
  return b === l ? `each win against it or one of its Boss Ladder steps drops a piece of one of its items ${b}% of the time`
    : `each win against it drops a piece of one of its items ${b}% of the time (${l}% on its Boss Ladder steps)`;
}

/** The Help page's held-items entry. */
export function heldHelp(): string {
  const boss = (b: string) => HELD_ITEMS.filter((d) => d.boss === b).map((d) => `${esc(d.name)} (${esc(heldRange(d.id))})`).join(', ');
  return `<h4>Held items</h4><p>A slot of its own on every creature, beside the Relic, that counts only in PvP: the Arena, Element Halls, friendly battles and training, tournaments and Trail trainers. Never on the hunt, against bosses or the Boss Ladder, or in Guild War. Put one on from the creature's page or from any line-up (the small slot on each creature); a swap sends the old one back to you.</p>
    <p>Each boss has four:</p><ul>${(['fenrir', 'typhon', 'hachimata'] as const).map((b) => `<li><b>${esc(BOSS_NAME(b))}</b>: ${boss(b)}.</li>`).join('')}</ul>
    <p>The Spirit Bead's element is chosen when it is forged.</p>
    <p>Each Trail path's trainers pay ten pieces of one item between them, and a boss's first clear on the Trail pays one whole item from its four. After that, ${pieceRate()}; ${PIECES_PER_HELD} pieces of one item forge it at the crafting bench, each forge with a fresh quality from 50% to 100% (high rolls are rare). Pieces and items trade on the Market; an item comes off its creature before it can be listed, and a creature listed, released or offered at the Altar gives its item back first.</p>`;
}
